import type { CountryISO3Code } from '@brightlocal/shared';
import {
  AUSTRALIA_ISO_CODE,
  AUSTRIA_ISO_CODE,
  BELGIUM_ISO_CODE,
  CANADA_ISO_CODE,
  FRANCE_ISO_CODE,
  GERMANY_ISO_CODE,
  GREECE_ISO_CODE,
  IRELAND_ISO_CODE,
  ITALY_ISO_CODE,
  NETHERLANDS_ISO_CODE,
  NEW_ZEALAND_ISO_CODE,
  PORTUGAL_ISO_CODE,
  SINGAPORE_ISO_CODE,
  SOUTH_AFRICA_ISO_CODE,
  SPAIN_ISO_CODE,
  SWEDEN_ISO_CODE,
  SWITZERLAND_ISO_CODE,
  TURKEY_ISO_CODE,
  UNITED_KINGDOM_ISO_CODE,
  USA_ISO_CODE
} from '@brightlocal/shared';

/** ISO alpha-3 country codes for the 6 core countries available to basic (non-CB) users */
export const CORE_COUNTRY_ISO_CODES: readonly CountryISO3Code[] = [
  USA_ISO_CODE,
  UNITED_KINGDOM_ISO_CODE,
  CANADA_ISO_CODE,
  AUSTRALIA_ISO_CODE,
  IRELAND_ISO_CODE,
  NEW_ZEALAND_ISO_CODE
];

/** Set of the 6 core country ISO-3 codes for O(1) lookup — used to gate basic (non-CB) users */
export const CORE_COUNTRY_CODES = new Set(CORE_COUNTRY_ISO_CODES);

/**
 * ISO alpha-3 codes of the countries Reviews works in.
 * TODO: read from the backend supported countries [https://brightlocal.atlassian.net/browse/FP-161]
 */
export const REVIEWS_COUNTRY_CODES: ReadonlySet<CountryISO3Code> = new Set([
  USA_ISO_CODE,
  UNITED_KINGDOM_ISO_CODE,
  CANADA_ISO_CODE,
  AUSTRALIA_ISO_CODE
]);

/** ISO alpha-3 country codes for which we support location creation (countries with at least one product capability) */
export const SUPPORTED_COUNTRY_CODES = new Set([
  AUSTRALIA_ISO_CODE,
  AUSTRIA_ISO_CODE,
  BELGIUM_ISO_CODE,
  CANADA_ISO_CODE,
  FRANCE_ISO_CODE,
  GERMANY_ISO_CODE,
  GREECE_ISO_CODE,
  IRELAND_ISO_CODE,
  ITALY_ISO_CODE,
  NETHERLANDS_ISO_CODE,
  NEW_ZEALAND_ISO_CODE,
  PORTUGAL_ISO_CODE,
  SINGAPORE_ISO_CODE,
  SOUTH_AFRICA_ISO_CODE,
  SPAIN_ISO_CODE,
  SWEDEN_ISO_CODE,
  SWITZERLAND_ISO_CODE,
  TURKEY_ISO_CODE,
  UNITED_KINGDOM_ISO_CODE,
  USA_ISO_CODE
]);

/**
 * Level of BrightLocal support a country has.
 * Drives whether we run reports, run a Citation Builder campaign only, or block creation.
 */
export const COUNTRY_SUPPORT_LEVEL = {
  /** Full experience — reports are supported (the 6 core countries). */
  CORE: 'core',
  /** Citation Builder campaign only — reports are not supported (the extended countries). */
  EXTENDED: 'extended',
  /** Not supported for location creation. */
  UNSUPPORTED: 'unsupported'
} as const;

/** URL to the supported countries page on Help Center */
export const SUPPORTED_COUNTRIES_URL =
  'https://help.brightlocal.com/hc/en-us/articles/203103168-In-which-countries-do-BrightLocal-s-tools-work';

/**
 * Google Form for requesting BrightLocal support in an unsupported / extended country.
 * Full `viewform` URL (not the forms.gle short link) so query-param prefills survive.
 */
export const COUNTRY_WAITING_LIST_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSdsHcAp_SJu3O452kw7srblRkZmGh7Og2N8jijLUgYtjgxXLA/viewform';

/** Prefill entry ids for {@link COUNTRY_WAITING_LIST_URL} */
export const COUNTRY_WAITING_LIST_FORM_ENTRIES = {
  FULL_NAME: 'entry.888124651',
  EMAIL: 'entry.784142003',
  COUNTRY: 'entry.1559999733'
} as const;

/**
 * Google Forms query param that marks the link as opened via a prefilled URL
 * (`usp` = usage source, `pp_url` = prepopulated from URL). Required for entry
 * prefills to apply when the form loads.
 */
export const COUNTRY_WAITING_LIST_PREFILL_USP = {
  KEY: 'usp',
  VALUE: 'pp_url'
} as const;

/**
 * Pages that can show an unavailable screen: the unsupported-country screen, or a plan screen from
 * the tool gate. Drives which tool name the screen copy uses, and the page title unless the page passes
 * its own `titleKey` to the gate.
 */
export const LOCATION_DATA_UNAVAILABLE_PAGE = {
  LOCAL_SEARCH_GRID: 'localSearchGrid',
  LOCAL_RANK_TRACKER: 'localRankTracker',
  LOCATION_SUMMARY: 'locationSummary',
  AI_VISIBILITY: 'aiVisibility',
  REVIEW_TRACKER: 'reviewTracker',
  REVIEW_MANAGER: 'reviewManager'
} as const;

/**
 * Sidebar label key that names the tool on the unavailable screens, per page. It is also the default page
 * title; a page under the tool passes its own `titleKey` to the gate instead.
 */
export const DATA_UNAVAILABLE_TOOL_LABEL_KEY = {
  [LOCATION_DATA_UNAVAILABLE_PAGE.LOCAL_SEARCH_GRID]: 'appShell.nav.localSearchGrid',
  [LOCATION_DATA_UNAVAILABLE_PAGE.LOCAL_RANK_TRACKER]: 'appShell.nav.localRankTracker',
  [LOCATION_DATA_UNAVAILABLE_PAGE.LOCATION_SUMMARY]: 'appShell.nav.locationDashboard',
  [LOCATION_DATA_UNAVAILABLE_PAGE.AI_VISIBILITY]: 'appShell.nav.aiVisibility',
  [LOCATION_DATA_UNAVAILABLE_PAGE.REVIEW_TRACKER]: 'appShell.nav.reviewTracker',
  [LOCATION_DATA_UNAVAILABLE_PAGE.REVIEW_MANAGER]: 'appShell.nav.reviewManager'
} as const;

/**
 * GTM `eventAction` for CTAs on the shared data-unavailable splash screen.
 * Distinguishes splash clicks from the same event names on other surfaces
 * (e.g. Locations Hub `add_location_clicked` with `eventAction: null`).
 */
export const DATA_UNAVAILABLE_ACTION = {
  DATA_UNAVAILABLE: 'data_unavailable'
} as const;
